import {useMemo} from 'react';

import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ConditionGroup} from 'studies/typed/shared/useExperimentDesignState';

import {MultiProcedureRunStudyDesignTabContentsQuery_procedureRun_ProcedureRunNode as ProcedureRunNode} from './__generated/MultiProcedureRunStudyDesignTabContentsQuery';
import {getActiveMethod, MethodIdAndName, resolveTasklessStatuses} from './activeMethodUtils';
import {
  getConditionReplicateEdges,
  getMethodExecutionStatuses,
  MethodExecutionStatus,
} from './getStudyDesignFromProcedureRun';
import {
  buildConditionGroupIndexByConditionId,
  StatusByNodeId,
} from './ProcedureRunDesignFlowchartLayoutUtils';
import {getProcedureRunDesignFlowchartNodeId} from './ProcedureRunDesignFlowchartNodeIdUtils';
import {PLANNED, type UnitOperationStatusType} from './procedureRunOverviewUtils';

// Stable empty references for when neither projection needs statuses, so memoization holds across renders.
const EMPTY_STATUSES: Array<MethodExecutionStatus> = [];
const EMPTY_STATUS_BY_NODE_ID: StatusByNodeId = new Map<string, UnitOperationStatusType>();
const EMPTY_RESOLVED: ReadonlyMap<string, ProcessTaskStatusType | null> = new Map();

/**
 * Builds a map from flowchart node id (`${conditionIndex}-${replicateIndex}-${methodId}`) to
 * the cell's method task status. A PMEI with no resolved status is marked `PLANNED`;
 * cells with no matching PMEI (e.g. not yet persisted) are left unset (no status icon).
 */
function buildStatusByNodeId(
  statuses: Array<MethodExecutionStatus>,
  conditions: Array<ConditionGroup>,
  resolvedStatuses: ReadonlyMap<string, ProcessTaskStatusType | null>
): StatusByNodeId {
  const conditionGroupIndexByConditionId = buildConditionGroupIndexByConditionId(conditions);
  const statusByNodeId = new Map<string, UnitOperationStatusType>();
  statuses.forEach(({pmeiId, methodId, conditionId, replicateNumber}) => {
    const conditionIndex = conditionGroupIndexByConditionId.get(conditionId);
    if (conditionIndex == null) {
      return;
    }
    statusByNodeId.set(
      getProcedureRunDesignFlowchartNodeId({
        conditionIndex,
        replicateIndex: replicateNumber - 1,
        methodId,
      }),
      resolvedStatuses.get(pmeiId) ?? PLANNED
    );
  });
  return statusByNodeId;
}

export interface UseProcedureRunMethodStatusesProps {
  procedureRun: ProcedureRunNode;
  methods: Array<MethodIdAndName>;
  conditions: Array<ConditionGroup>;
  isFlowchartProcedureRunDesignEnabled: boolean;
  isSplitPoolEnabled: boolean;
}

export interface UseProcedureRunMethodStatusesResult {
  statusByNodeId: StatusByNodeId;
  activeMethod: MethodIdAndName | null;
  doesActiveMethodHaveDownstreamMethods: boolean;
}

/**
 * Derives method task statuses for a procedure run's design tab, projecting a single per-PMEI list
 * two ways: the active method (a flow-order pointer) and a per-cell status map for the flowchart.
 *
 * Each projection is gated by its own flag:
 * - The per-cell map only renders in the flowchart UI (`isFlowchartProcedureRunDesignEnabled`).
 * - The active method is only used when split-pool is enabled (`isSplitPoolEnabled`), including in
 *   the legacy (non-flowchart) UI where it gates editing.
 *
 * Both the per-PMEI list and active method depend only on the persisted `procedureRun`, so the
 * active method stays stable across grid edits. Only the per-cell map depends on `conditions`.
 */
export function useProcedureRunMethodStatuses({
  procedureRun,
  methods,
  conditions,
  isFlowchartProcedureRunDesignEnabled,
  isSplitPoolEnabled,
}: UseProcedureRunMethodStatusesProps): UseProcedureRunMethodStatusesResult {
  // The per-PMEI list feeds both projections, so compute it only when at least one is needed.
  const areStatusesNeeded = isFlowchartProcedureRunDesignEnabled || isSplitPoolEnabled;
  const statuses = useMemo(
    () => (areStatusesNeeded ? getMethodExecutionStatuses(procedureRun) : EMPTY_STATUSES),
    [areStatusesNeeded, procedureRun]
  );

  const activeMethod = useMemo(() => {
    if (!isSplitPoolEnabled) {
      return null;
    }
    return getActiveMethod(methods, statuses);
  }, [isSplitPoolEnabled, methods, statuses]);

  const doesActiveMethodHaveDownstreamMethods = useMemo(() => {
    if (activeMethod == null) {
      return false;
    }
    const activeUnitOpIndex = methods.findIndex((method) => method.id === activeMethod.id);
    return activeUnitOpIndex >= 0 && activeUnitOpIndex < methods.length - 1;
  }, [activeMethod, methods]);

  const resolvedStatuses = useMemo(() => {
    if (!isFlowchartProcedureRunDesignEnabled) {
      return EMPTY_RESOLVED;
    }
    const statusMap = new Map(statuses.map((s) => [s.pmeiId, s.status]));
    const edges = getConditionReplicateEdges(procedureRun);
    const pmeiMethodIds = new Map(statuses.map((s) => [s.pmeiId, s.methodId]));
    return resolveTasklessStatuses(statusMap, edges, methods, pmeiMethodIds);
  }, [isFlowchartProcedureRunDesignEnabled, statuses, procedureRun, methods]);

  const statusByNodeId = useMemo(
    () =>
      isFlowchartProcedureRunDesignEnabled
        ? buildStatusByNodeId(statuses, conditions, resolvedStatuses)
        : EMPTY_STATUS_BY_NODE_ID,
    [isFlowchartProcedureRunDesignEnabled, statuses, conditions, resolvedStatuses]
  );

  return {statusByNodeId, activeMethod, doesActiveMethodHaveDownstreamMethods};
}
